import { ChangeEvent, FormEvent, useState } from "react";

type ContactValues = {
  name: string;
  email: string;
  message: string;
};

type ContactStatus = "idle" | "loading" | "success" | "error";

const initialValues: ContactValues = {
  name: "",
  email: "",
  message: "",
};

export const useContact = () => {
  const [values, setValues] = useState<ContactValues>(initialValues);
  const [status, setStatus] = useState<ContactStatus>("idle");
  const [error, setError] = useState("");

  const updateField =
    (field: keyof ContactValues) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValues((current) => ({ ...current, [field]: event.target.value }));
      if (status !== "idle") {
        setStatus("idle");
        setError("");
      }
    };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus("loading");
    setError("");

    try {
      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.error || "Unable to send your message.");
      }

      setValues(initialValues);
      setStatus("success");
    } catch (requestError) {
      setStatus("error");
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Something went wrong. Please try again."
      );
    }
  };

  return {
    values,
    status,
    error,
    isSubmitting: status === "loading",
    updateField,
    submit,
  };
};
